import { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CheckCircle2, Sparkles, HeartHandshake, Award, Briefcase } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const RECRUIT_IMG =
  "https://static.prod-images.emergentagent.com/jobs/d4897236-428b-4abf-b3c0-e7679c5a4973/images/8b4cd03234514d48a2bd24fa51921a96e690fd2396b7f3b550485891ad655638.png";

const PERKS = [
  { icon: Sparkles, t: "Flexible scheduling", b: "Real work-life balance — shifts that fit your life." },
  { icon: HeartHandshake, t: "Supportive culture", b: "A team that respects, listens, and has your back." },
  { icon: Award, t: "Meaningful work", b: "Care that genuinely changes lives in Madison." },
  { icon: Briefcase, t: "Career growth", b: "Training, mentorship, and a real path forward." },
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Recruitment({ standalone = false }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    position: "",
    years_experience: "",
    certifications: "",
    availability: "",
    why_join: "",
    resume_url: "",
    references: "",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.position) {
      toast.error("Please fill in your name, email, phone, and position.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/caregiver-application`, form);
      toast.success("Application received! We'll be in touch soon.");
      setForm({
        full_name: "", email: "", phone: "", position: "",
        years_experience: "", certifications: "", availability: "", why_join: "",
        resume_url: "", references: "",
      });
    } catch (err) {
      toast.error("Something went wrong. Please call us at 608-513-7639.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="careers"
      data-testid="recruitment-section"
      className={`relative ${standalone ? 'pt-32 pb-24' : 'py-24 md:py-32'} bg-[#F8F9FA]/40 overflow-hidden`}
    >
      <div className="absolute inset-0 grain" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-[0_30px_60px_-20px_rgba(15,23,42,0.25)]">
            <img
              src={RECRUIT_IMG}
              alt="Smiling caregiver representing the Madison Peaceful Path team"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div className="mt-8">
            <span className="gold-divider mb-4">Join Our Team</span>
            <h2 className="font-serif text-4xl md:text-5xl font-light text-[#005165] leading-[1.1]">
              Caregivers deserve respect, support, and <em className="not-italic text-[#AC9667]">appreciation</em> too.
            </h2>
            <p className="mt-5 text-[17px] text-[#4B5563] leading-relaxed">
              We're building a team of compassionate professionals — CNAs, caregivers, and med techs —
              who believe in dignity-centered care. If that's you, we want to meet you.
            </p>
            <ul className="mt-6 space-y-3">
              {PERKS.map((p) => (
                <li key={p.t} className="flex items-start gap-3">
                  <span className="w-10 h-10 rounded-xl bg-white border border-[#E5DDC9] flex items-center justify-center shrink-0">
                    <p.icon className="w-4 h-4 text-[#AC9667]" strokeWidth={1.7} />
                  </span>
                  <div>
                    <p className="font-medium text-[#005165]">{p.t}</p>
                    <p className="text-sm text-[#4B5563]">{p.b}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Application form */}
        <div className="lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid="caregiver-application-form"
            className="bg-white rounded-3xl p-8 md:p-10 border border-[#E5DDC9]/70 shadow-[0_20px_60px_-30px_rgba(15,23,42,0.2)]"
          >
            <h3 className="font-serif text-3xl text-[#005165] mb-1">Apply Now</h3>
            <p className="text-sm text-[#64748B] mb-7">
              Tell us a bit about you — we'll reach out personally.
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="cv-name">Full Name *</Label>
                <Input id="cv-name" data-testid="cv-name" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className="mt-1.5 bg-[#FFFFFF] border-[#E5DDC9] focus-visible:ring-[#AC9667]" />
              </div>
              <div>
                <Label htmlFor="cv-email">Email *</Label>
                <Input id="cv-email" data-testid="cv-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="mt-1.5 bg-[#FFFFFF] border-[#E5DDC9] focus-visible:ring-[#AC9667]" />
              </div>
              <div>
                <Label htmlFor="cv-phone">Phone *</Label>
                <Input id="cv-phone" data-testid="cv-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="mt-1.5 bg-[#FFFFFF] border-[#E5DDC9] focus-visible:ring-[#AC9667]" />
              </div>
              <div>
                <Label htmlFor="cv-position">Position *</Label>
                <Select value={form.position} onValueChange={(v) => update("position", v)}>
                  <SelectTrigger id="cv-position" data-testid="cv-position" className="mt-1.5 bg-[#FFFFFF] border-[#E5DDC9]">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CNA">CNA</SelectItem>
                    <SelectItem value="Caregiver">Caregiver</SelectItem>
                    <SelectItem value="Med Tech">Med Tech</SelectItem>
                    <SelectItem value="Companion">Companion</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="cv-exp">Years of Experience</Label>
                <Input id="cv-exp" data-testid="cv-exp" value={form.years_experience} onChange={(e) => update("years_experience", e.target.value)} className="mt-1.5 bg-[#FFFFFF] border-[#E5DDC9]" />
              </div>
              <div>
                <Label htmlFor="cv-cert">Certifications</Label>
                <Input id="cv-cert" data-testid="cv-cert" placeholder="CPR, CNA, etc." value={form.certifications} onChange={(e) => update("certifications", e.target.value)} className="mt-1.5 bg-[#FFFFFF] border-[#E5DDC9]" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="cv-avail">Availability</Label>
                <Input id="cv-avail" data-testid="cv-avail" placeholder="Days, evenings, weekends..." value={form.availability} onChange={(e) => update("availability", e.target.value)} className="mt-1.5 bg-[#FFFFFF] border-[#E5DDC9]" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="cv-why">Why do you want to join us?</Label>
                <Textarea id="cv-why" data-testid="cv-why" rows={4} value={form.why_join} onChange={(e) => update("why_join", e.target.value)} className="mt-1.5 bg-[#FFFFFF] border-[#E5DDC9]" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="cv-resume">Resume link (Google Drive, Dropbox, LinkedIn, etc.)</Label>
                <Input id="cv-resume" data-testid="cv-resume" placeholder="https://..." value={form.resume_url} onChange={(e) => update("resume_url", e.target.value)} className="mt-1.5 bg-[#FFFFFF] border-[#E5DDC9]" />
                <p className="text-xs text-[#4B5563] mt-1.5">Paste a shareable link to your resume and/or certifications. We'll request originals during the interview.</p>
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="cv-refs">References (optional)</Label>
                <Textarea id="cv-refs" data-testid="cv-refs" rows={3} placeholder="Name, role, phone or email" value={form.references} onChange={(e) => update("references", e.target.value)} className="mt-1.5 bg-[#FFFFFF] border-[#E5DDC9]" />
              </div>
            </div>

            <Button
              data-testid="cv-submit"
              type="submit"
              disabled={loading}
              className="mt-7 w-full sm:w-auto bg-[#005165] hover:bg-[#003946] text-[#FFFFFF] rounded-full h-13 px-10 py-4 btn-shimmer"
            >
              {loading ? "Submitting..." : "Submit Application"}
            </Button>

            <p className="mt-4 text-xs text-[#64748B] flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#AC9667]" /> We respect your privacy. We'll never share your information.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}
